import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';

/**
 * `<nb-bottom-nav-item>` — a single tab in the bottom nav (Mockup `.navitem`).
 *
 * Renders a real `<button>` so it is keyboard operable and announced as a
 * control. Project an `<nb-icon>` (sized to 24px) and supply a `label`; the
 * label renders below the icon and doubles as the button's accessible name.
 * `active` is a two-way `model()` — when set the top accent pill (`::before`)
 * shows and the colour switches from `--faint` to `--text`; `aria-current`
 * pairs the colour change so active state is never colour-only. An optional
 * `count` renders the dot badge and is folded into the accessible name.
 *
 * @example
 * <nb-bottom-nav-item label="Varsler" [count]="3" [(active)]="isNotif" (activated)="go('notif')">
 *   <nb-icon [icon]="Bell"/>
 * </nb-bottom-nav-item>
 */
@Component({
  selector: 'nb-bottom-nav-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<button
    class="navitem"
    [class.active]="active()"
    type="button"
    [attr.aria-current]="active() ? 'page' : null"
    [attr.aria-label]="ariaLabel()"
    (click)="onClick()"
  >
    <ng-content />
    @if (showDot()) {
      <span class="dot" aria-hidden="true">{{ badge() }}</span>
    }
    <span>{{ label() }}</span>
  </button>`,
  styleUrl: './bottom-nav.css',
})
export class NbBottomNavItem {
  /** Text label shown beneath the icon; also the accessible name. */
  readonly label = input.required<string>();
  /** Whether this tab is the current page. Two-way bindable. */
  readonly active = model(false);
  /** Optional unread count rendered as a dot badge (omit / 0 hides it). */
  readonly count = input<number>();

  /** Emits when the tab is activated (click / keyboard). */
  readonly activated = output<void>();

  protected readonly showDot = computed(() => (this.count() ?? 0) > 0);
  protected readonly badge = computed(() => {
    const n = this.count() ?? 0;
    return n > 99 ? '99+' : String(n);
  });
  protected readonly ariaLabel = computed(() => {
    const n = this.count() ?? 0;
    return n > 0 ? `${this.label()}, ${n} uleste` : this.label();
  });

  protected onClick(): void {
    this.active.set(true);
    this.activated.emit();
  }
}
